
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { AdminLayout } from '@/components/admin/layout/AdminLayout';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BarChart3, FileText, Users, Settings, MessageSquareQuote } from 'lucide-react';

// New component for the admin dashboard stats card
const StatsCard = ({ 
  title, 
  count, 
  icon: Icon, 
  path, 
  description 
}: { 
  title: string; 
  count: number | null; 
  icon: React.ElementType; 
  path: string; 
  description: string;
}) => {
  const navigate = useNavigate();
  
  return (
    <Card 
      className="relative overflow-hidden transition-all hover:shadow-md cursor-pointer group"
      onClick={() => navigate(path)}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
            <h3 className="text-2xl font-bold">
              {count !== null ? count : (
                <div className="h-8 w-16 bg-muted animate-pulse rounded" />
              )}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">{description}</p>
          </div>
          <div className="bg-primary/10 p-3 rounded-full">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 h-1 w-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all duration-500 w-0 group-hover:w-full" />
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  // Fetch summary data for dashboard
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: async () => {
      try {
        console.log('Fetching admin dashboard stats...');
        
        // Using raw SQL queries with RPC will bypass RLS for admins
        // Count all applications regardless of status
        const { data: allApplications, error: applicationsError } = await supabase
          .from('applications')
          .select('id');
          
        if (applicationsError) {
          console.error('Error fetching applications:', applicationsError);
          throw applicationsError;
        }
        
        // Count all quotes
        const { data: allQuotes, error: quotesError } = await supabase
          .from('peo_quotes')
          .select('id');
          
        if (quotesError) {
          console.error('Error fetching quotes:', quotesError);
          throw quotesError;
        }
        
        // Count all clients
        const { data: allClients, error: clientsError } = await supabase
          .from('clients')
          .select('id');
          
        if (clientsError) {
          console.error('Error fetching clients:', clientsError);
          throw clientsError;
        }
        
        // Count all PEO providers
        const { data: allProviders, error: providersError } = await supabase
          .from('peo_providers')
          .select('id');
          
        if (providersError) {
          console.error('Error fetching providers:', providersError);
          throw providersError;
        }
        
        // Count all users
        const { data: allUsers, error: usersError } = await supabase
          .from('profiles')
          .select('id');
          
        if (usersError) {
          console.error('Error fetching users:', usersError);
          throw usersError;
        }
        
        console.log('Applications count:', allApplications ? allApplications.length : 0);
        
        return {
          providers: allProviders ? allProviders.length : 0,
          applications: allApplications ? allApplications.length : 0,
          quotes: allQuotes ? allQuotes.length : 0,
          clients: allClients ? allClients.length : 0,
          users: allUsers ? allUsers.length : 0
        };
      } catch (error) {
        console.error('Error fetching admin stats:', error);
        return {
          providers: 0,
          applications: 0,
          quotes: 0,
          clients: 0,
          users: 0
        };
      }
    },
    staleTime: 60000 // 1 minute
  });
  
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <div className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>
        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <StatsCard
            title="PEO Providers"
            count={isLoading ? null : stats?.providers}
            icon={Settings}
            path="/admin/peo"
            description="Active providers in system"
          />

          <StatsCard
            title="Applications"
            count={isLoading ? null : stats?.applications}
            icon={FileText}
            path="/admin/applications"
            description="Total submitted applications"
          />

          <StatsCard
            title="Quotes"
            count={isLoading ? null : stats?.quotes}
            icon={MessageSquareQuote}
            path="/admin/quotes"
            description="PEO quotes generated"
          />

          <StatsCard
            title="Clients"
            count={isLoading ? null : stats?.clients}
            icon={BarChart3}
            path="/admin/clients"
            description="Businesses with active service"
          />

          <StatsCard
            title="Users"
            count={isLoading ? null : stats?.users}
            icon={Users}
            path="/admin/users"
            description="Registered user accounts"
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
